import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";
import sortBy from "lodash/sortBy";
import { getRowFromDataItem } from "PFApp/booking/parts/helpers";
import { usePastDatesStateContext } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/past_dates_context";
import { checkIsExpandable } from "PFApp/booking/parts/overview/side_panel_row/side_panel_row.utils";
import {
  useBookingModuleStore,
  useCalendarRoot,
  useCalendarRootActions,
  useWorkforceProfilesDataIfAvailable
} from "PFApp/booking/store";
import {
  calculateAllBookingsModeState,
  checkIsAllBookingsModeApplicable
} from "PFApp/booking/store/provider/calendar_root_rows/calculate_all_bookings_mode_state";
import { useCalendarRootData } from "PFApp/booking/store/provider/calendar_root_rows/use_calendar_root_data";
import { CalendarView, Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { EngagementWallPlannerMode } from "PFApp/booking/types/engagement_wallplanner_mode";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";
import { JsonValue, stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { ProjectItem } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { useEffect } from "react";

import { appendChildrenToRows, useChildrenInPast, useRowsChildren } from "../children_rows";
import { calculateExpandedState } from "./calculate_expanded_state";

export const useCalendarRootRows = () => {
  const {
    data: { templates }
  } = useMyAccountInfo();
  const { pastFetchesCount } = usePastDatesStateContext();

  const calendarRoot = useCalendarRoot();
  const pinnedIds = useBookingModuleStore((state) => state[calendarRoot].pinnedIds);
  const rows = useBookingModuleStore((state) => state[calendarRoot].rows);
  const expandedRowsState = useBookingModuleStore((state) => state[calendarRoot].expandedRowsState);
  const childrenInPastByRowKey = useBookingModuleStore((state) => state[calendarRoot].childrenInPastByRowKey);
  const isRoleLevelHiddenForEngagementsRoot = useBookingModuleStore(
    (state) => state[CalendarRoot.ProjectsEngagements].isRoleLevelHidden
  );
  const { view } = useWorkforceProfilesDataIfAvailable("view");
  const allBookingsModeRowsState = useBookingModuleStore((state) =>
    isProjects(calendarRoot) ? state[calendarRoot].allBookingsModeRowsState : null
  );
  const setAllBookingsModeRowsState = useBookingModuleStore((state) =>
    isProjects(calendarRoot) ? state.actions[calendarRoot].setAllBookingsModeRowsState : null
  );
  const isWrapModeEnabledForEngagementsRoot = useBookingModuleStore(
    (state) =>
      calendarRoot === CalendarRoot.ProjectsEngagements &&
      state[CalendarRoot.ProjectsEngagements].engagementWallplannerMode === EngagementWallPlannerMode.Wrap
  );
  const {
    setExpandedRowsState,
    setRowsLoadingByKey,
    setRows,
    setPaginationMetaByRowKey,
    setHasChildrenInPastByRowKey,
    resetHasChildrenInPast
  } = useCalendarRootActions(calendarRoot);

  const { data, pinnedData } = useCalendarRootData();

  const { childrenDataByRowKey, childrenFetchingByRowKey } = useRowsChildren();
  const { hasChildrenInPastByRowKey, fetchingByRowKey: childrenInPastFetching } = useChildrenInPast({
    calendarRoot,
    rows,
    childrenInPastByRowKey,
    childrenDataByRowKey,
    pastFetchesCount
  });

  useEffect(() => {
    if (view === CalendarView.BookingsOnly) {
      setExpandedRowsState({});
    }
  }, [view]);

  useEffect(() => {
    const loadingKey = "childrenLoading";
    const loadingState = mapValues(childrenFetchingByRowKey, (value) => ({ [loadingKey]: value }));
    setRowsLoadingByKey(loadingState, loadingKey);
  }, [stringify({ childrenFetchingByRowKey })]);

  useEffect(() => {
    const loadingKey = "hasChildrenInPastLoading";
    const loadingState = mapValues(childrenInPastFetching, (value) => ({ [loadingKey]: value }));
    setRowsLoadingByKey(loadingState, loadingKey);
  }, [stringify({ childrenInPastFetching })]);

  useEffect(() => {
    resetHasChildrenInPast();
  }, [pastFetchesCount]);

  useEffect(() => {
    setHasChildrenInPastByRowKey(hasChildrenInPastByRowKey);
  }, [stringify({ hasChildrenInPastByRowKey })]);

  useEffect(() => {
    const pinnedEntries = sortBy<WorkforceItem | ProjectItem>(pinnedData?.entries, ({ id }) =>
      (pinnedIds || []).indexOf(id)
    );
    const pinnedEntryIds = pinnedEntries.map(({ id }) => id);
    const newPinnedRootRows = pinnedEntries.map((entry) =>
      getRowFromDataItem(entry, templates, calendarRoot)
    );
    const newRegularRootRows = (data?.entries ?? [])
      .filter(({ id }) => !pinnedEntryIds.includes(id)) // hiding fetched pinned entries
      .map((entry) => getRowFromDataItem(entry, templates, calendarRoot));

    const { rowsWithChildren: pinnedRowsWithChildren, childrenMetaByParentRowKey: pinnedChildrenMeta } =
      appendChildrenToRows({
        rootRows: newPinnedRootRows,
        calendarRoot,
        childrenDataByRowKey,
        templates,
        isRoleLevelHiddenForEngagementsRoot
      });
    const { rowsWithChildren: regularRowsWithChildren, childrenMetaByParentRowKey: regularChildrenMeta } =
      appendChildrenToRows({
        rootRows: newRegularRootRows,
        calendarRoot,
        childrenDataByRowKey,
        templates,
        isRoleLevelHiddenForEngagementsRoot
      });

    const newRows = [...pinnedRowsWithChildren, ...regularRowsWithChildren];

    const initialExpandedRowsState = isWrapModeEnabledForEngagementsRoot
      ? {
          ...expandedRowsState,
          // in wrap mode all expandable root rows should be automatically expanded
          ...mapValues(keyBy([...newRegularRootRows, ...newPinnedRootRows], "key"), (row) =>
            checkIsExpandable(calendarRoot, row, null)
          )
        }
      : expandedRowsState;
    const newExpandedState = calculateExpandedState<Row>({
      expandedState: initialExpandedRowsState,
      items: newRows,
      isExpandable: (row) => checkIsExpandable(calendarRoot, row, view),
      forceExpandNewChildren: !isWrapModeEnabledForEngagementsRoot
    });

    setRows(newRows);
    setPaginationMetaByRowKey(merge({}, pinnedChildrenMeta, regularChildrenMeta));
    setExpandedRowsState(newExpandedState);

    if (isProjects(calendarRoot)) {
      const newAllBookingsModeState = calculateAllBookingsModeState<Row>({
        allBookingsModeState: allBookingsModeRowsState ?? {},
        items: newRows,
        isApplicable: (row) => checkIsAllBookingsModeApplicable(row, calendarRoot)
      });
      setAllBookingsModeRowsState?.(newAllBookingsModeState);
    }
  }, [
    stringify({
      pinnedEntries: pinnedData?.entries,
      dataEntries: data?.entries,
      childrenDataByRowKey
    } as JsonValue)
  ]);
};
